import React from "react";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { useDispatch } from "src/store";
import { Tooltip } from "src/ui/shared/Tooltip";

import { updateGroupMembers } from "@dashboard/devices/store";
import { Group, GroupMember } from "@dashboard/devices/types";
import { RoleLabel } from "@dashboard/shared/components/RoleLabel";
import { getAssignableRoleOptions, rolesOptions } from "@dashboard/shared/roles";
import { memberSubject, PermissionService } from "@services/permissionService/permissionService";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { useCurrentWorkspace } from "../../api/helpers";

function validateRole(role: unknown) {
    if (role !== "Owner" && role !== "Manager" && role !== "User") {
        throw new Error(i18n.t("pages:workspacePage.toast.invalidRole"));
    }
    return role;
}

type Props = {
    group: Group;
    member: GroupMember;
};

export const ChangeGroupRoleField = (props: Props) => {
    const [loading, setLoading] = React.useState(false);

    const { t } = useTranslation(["common", "pages", "modals", "sidepanels"]);
    const dispatch = useDispatch();

    const { data: workspace } = useCurrentWorkspace();
    const { groupAbility } = PermissionService();
    const ability = groupAbility(props.group, workspace);
    const mayManageMember = ability.can("manage", "Member");
    const canAssignOwner = ability.can("manage", memberSubject("Owner"));
    const value = props.group.members.find((member) => member.email === props.member.email)?.role;

    const mayEditThisMember = ability.can("manage", memberSubject(value));
    const options = getAssignableRoleOptions(canAssignOwner);

    const getTooltip = () => {
        if (!mayEditThisMember) {
            return t("sidepanels:groupPanel.tooltip.cannotEditOwnerRole");
        }

        return null;
    };

    const onChangeRole: SingleSelectFieldProps["onChange"] = async (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "string") {
            return;
        }
        try {
            setLoading(true);
            const validatedRole = validateRole(selectedOption.value);
            await dispatch(
                updateGroupMembers({ id: props.group.id, changedMembers: { [props.member.email]: validatedRole } }),
            ).unwrap();
            showSuccessToast(t("pages:workspacePage.toast.roleChangedSuccess"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        } finally {
            setLoading(false);
        }
    };

    const tooltip = getTooltip();

    // A member who cannot manage members can never edit roles, so show the role as plain text.
    if (!mayManageMember) {
        return <RoleLabel role={value} />;
    }

    return (
        <Tooltip title={tooltip} disableChildrenWrapper>
            <SelectField
                value={rolesOptions.find((option) => option.value === value)}
                options={options}
                onChange={onChangeRole}
                disabled={loading || !!tooltip}
                fullWidth
                disableSorting
            />
        </Tooltip>
    );
};
